import { Injectable, inject } from '@angular/core';
import { AuthStore } from '../store/auth.store';
import { TokenStoreService } from '../auth/token-store.service';
import { LoggerService } from '../logger/logger.service';
import { TelemetryEventStoreService } from './telemetry-event-store.service';
import { TelemetryApiService } from './telemetry-api.service';
import { environment } from '../../../environments/environment';
import {
  DRAIN_LOCK_NAME,
  SYNC_DEBOUNCE_MS,
  SYNC_POLL_INTERVAL_MS,
  computeNextRetryDelayMs,
  normalizeTelemetryTimestamp,
  transformPayloadForApi,
} from './telemetry.types';
import type { TelemetryRecord, TelemetryApiEvent } from './telemetry.types';

const TAG = 'TelemetrySync';

/**
 * Orchestrates one drain cycle: reads pending records, POSTs them, marks
 * success/retry/dead-letter on the result. Also owns drain scheduling.
 *
 * Rules:
 *  - Never more than one concurrent drain across all tabs (Web Locks).
 *  - Never run while offline, unauthenticated, or when disabled by environment.
 *  - 401 → pause; never log out.
 *  - 412 → isolate the offending record to dead-letter, retry the rest.
 *  - Partial 200 → mark only returned successIds; retry the rest.
 */
@Injectable({ providedIn: 'root' })
export class TelemetrySyncService {
  private store = inject(TelemetryEventStoreService);
  private api = inject(TelemetryApiService);
  private authStore = inject(AuthStore);
  private tokenStore = inject(TokenStoreService);
  private logger = inject(LoggerService);

  private drainActive = false;
  private forceDrainRequested = false;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private scheduledDrainTimer: ReturnType<typeof setTimeout> | null = null;
  private paused = false; // true after a 401 until access token changes
  private failed401Token: string | null = null;
  private visibilityHandler: (() => void) | null = null;
  private onlineHandler: (() => void) | null = null;
  private vdiSessionActive = false;

  /**
   * Start the single periodic sync process if not already running.
   * Strictly idempotent: guarantees exactly ONE timer exists.
   */
  startPeriodicSync(): void {
    if (environment.telemetry && !environment.telemetry.enabled) return;
    if (this.pollTimer !== null) return; // Already running, prevent duplicate timers

    this.setupListeners();

    const intervalMs = environment.telemetry?.syncIntervalMs ?? SYNC_POLL_INTERVAL_MS;
    this.pollTimer = setInterval(() => {
      if (document.visibilityState === 'visible') {
        void this.drain();
      }
    }, intervalMs);

    this.logger.info(TAG, 'single periodic sync timer started', { intervalMs });
  }

  /**
   * Stop the single periodic sync process and enter IDLE state.
   */
  stopPeriodicSync(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
      this.logger.info(TAG, 'single periodic sync timer stopped (IDLE)');
    }
  }

  /** Mark VDI session state so auto-sleep logic knows whether VDI is active. */
  setVdiSessionActive(active: boolean): void {
    this.vdiSessionActive = active;
  }

  /** Returns true if the periodic timer is currently running. */
  isSyncActive(): boolean {
    return this.pollTimer !== null;
  }

  private setupListeners(): void {
    if (!this.onlineHandler) {
      this.onlineHandler = () => {
        this.logger.info(TAG, 'network online detected — triggering drain');
        void this.drain();
      };
      window.addEventListener('online', this.onlineHandler);
    }

    if (!this.visibilityHandler) {
      this.visibilityHandler = () => {
        if (document.visibilityState === 'visible') {
          void this.drain();
        }
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }
  }

  /** Backward-compatible alias for startPeriodicSync */
  startScheduling(): void {
    this.startPeriodicSync();
  }

  /** Backward-compatible alias for stopPeriodicSync */
  stopScheduling(): void {
    this.stopPeriodicSync();
    if (this.scheduledDrainTimer !== null) {
      clearTimeout(this.scheduledDrainTimer);
      this.scheduledDrainTimer = null;
    }
    if (this.onlineHandler) { window.removeEventListener('online', this.onlineHandler); this.onlineHandler = null; }
    if (this.visibilityHandler) { document.removeEventListener('visibilitychange', this.visibilityHandler); this.visibilityHandler = null; }
  }

  /** Resume the sync after an auth state change resolves a prior 401 pause. */
  resumeAfterAuth(): void {
    this.paused = false;
    this.failed401Token = null;
    void this.drain();
  }

  async drain(force = false): Promise<void> {
    if (environment.telemetry && !environment.telemetry.enabled) return;
    if (this.drainActive) {
      if (force) this.forceDrainRequested = true;
      this.logger.info(TAG, 'drain skipped: drain already active');
      return;
    }

    // Auto-unpause if access token has refreshed since prior 401
    const currentToken = this.tokenStore.getAccessToken();
    if (this.paused && currentToken && currentToken !== this.failed401Token) {
      this.logger.info(TAG, 'refreshed access token detected — unpausing telemetry sync');
      this.paused = false;
      this.failed401Token = null;
    }

    if (this.paused) {
      this.logger.warn(TAG, 'drain skipped: paused due to prior 401 (waiting for token refresh)');
      return;
    }
    if (!navigator.onLine) {
      this.logger.warn(TAG, 'drain skipped: navigator is offline');
      return;
    }
    if (!this.authStore.isAuthenticated()) {
      this.logger.warn(TAG, 'drain skipped: user not authenticated (no active access token)');
      return;
    }

    if (!('locks' in navigator)) {
      // Web Locks unavailable — drain without cross-tab serialisation.
      await this.doDrain(force);
      return;
    }

    try {
      await navigator.locks.request(DRAIN_LOCK_NAME, { ifAvailable: true }, async (lock) => {
        if (!lock) {
          this.logger.info(TAG, 'drain skipped: lock held by another instance');
          return;
        }
        await this.doDrain(force);
      });
    } catch (err) {
      this.logger.warn(TAG, 'Web Locks request failed', err);
    }
  }

  private async doDrain(force: boolean): Promise<void> {
    if (this.drainActive) return;
    this.drainActive = true;
    let hasMore = false;

    try {
      const records = await this.store.getPendingBatch(force);
      if (!records.length) {
        this.logger.info(TAG, 'no pending records eligible for drain');
        if (!this.vdiSessionActive) {
          this.stopPeriodicSync();
        }
      } else {
        this.logger.info(TAG, `found ${records.length} pending record(s) to drain`, { count: records.length });

        const ids = records.filter((r) => r.id != null).map((r) => r.id as number);
        await this.store.acquireLeases(ids);

        const body: TelemetryApiEvent[] = records.map((r) => ({
          id: r.id as number,
          eventType: r.eventType,
          timestamp: normalizeTelemetryTimestamp(r.timestamp),
          payload: transformPayloadForApi(r.payload),
        }));

        try {
          this.logger.info(TAG, 'sending batch to POST /api/v2/telemetry/events', { eventCount: body.length });
          const response = await this.api.sendBatch(body);

          if (response?.success) {
            const successIds = response.data?.successIds ?? [];
            const successSet = new Set(successIds);
            const retryIds = ids.filter((id) => !successSet.has(id));

            await this.store.markSynced(successIds);
            if (retryIds.length) {
              const sampleRecord = records.find((r) => r.id != null && retryIds.includes(r.id as number));
              const delay = computeNextRetryDelayMs(sampleRecord?.attemptCount ?? 0);
              await this.store.markRetry(retryIds, delay, 'not in successIds');
            }

            const pendingCount = await this.store.getPendingCount();
            hasMore = pendingCount > 0;
            this.logger.info(TAG, 'drain complete', { synced: successIds.length, retried: retryIds.length, remaining: pendingCount });

            // Auto-sleep: If queue is completely cleared and VDI is not active, stop the timer
            if (pendingCount === 0 && !this.vdiSessionActive) {
              this.stopPeriodicSync();
            }
          } else {
            // Non-success 200 — treat all as retry
            const delay = computeNextRetryDelayMs(0);
            await this.store.markRetry(ids, delay, response?.message ?? 'non-success response');
            this.logger.warn(TAG, 'drain returned non-success response', response);
          }
        } catch (err: unknown) {
          await this.handleDrainError(err, records, ids);
        }
      }
    } finally {
      this.drainActive = false;
    }

    const runForcedFollowUp = this.forceDrainRequested;
    this.forceDrainRequested = false;
    if (hasMore || runForcedFollowUp) {
      // Continuous Drain Loop: If more records exist in the backlog (> 100), immediately process next batch
      void this.drain(force || runForcedFollowUp);
    }
  }

  /**
   * Trigger one debounced drain. This allows real-time VDI mode to batch events
   * received in a short burst without waiting for the periodic scheduler.
   */
  scheduleDrain(): void {
    if (environment.telemetry && !environment.telemetry.enabled) return;
    if (this.scheduledDrainTimer !== null) return;

    const delayMs = environment.telemetry?.syncDebounceMs ?? SYNC_DEBOUNCE_MS;
    this.scheduledDrainTimer = setTimeout(() => {
      this.scheduledDrainTimer = null;
      void this.drain();
    }, delayMs);
  }

  private async handleDrainError(
    err: unknown,
    records: TelemetryRecord[],
    ids: number[],
  ): Promise<void> {
    const status = this.extractHttpStatus(err);

    if (status === 401) {
      // Pause sync until access token is refreshed
      this.paused = true;
      this.failed401Token = this.tokenStore.getAccessToken();
      await this.store.releaseLeases(ids);
      this.logger.warn(TAG, 'telemetry 401 — sync paused until next token refresh');
      return;
    }

    if (status === 412) {
      if (records.length === 1) {
        // Isolated single offending record: move to dead letter
        this.logger.warn(TAG, 'telemetry 412 — isolating corrupt record to dead-letter', { id: records[0].id });
        await this.store.moveToDeadLetter(records[0], 'HTTP 412');
      } else {
        // Multi-record batch: split and retry with backoff to isolate the single corrupt record
        this.logger.warn(TAG, `telemetry 412 on batch of ${records.length} records — splitting batch for individual isolation`);
        await this.store.releaseLeases(ids);
        await this.store.markRetry(ids, 0, 'HTTP 412 batch split');
      }
      return;
    }

    // Network error, timeout, 429, 5xx — retry with backoff.
    const sampleRecord = records[0];
    const delay = computeNextRetryDelayMs(sampleRecord?.attemptCount ?? 0);
    await this.store.markRetry(ids, delay, `HTTP ${status ?? 'network'}`);
    this.logger.warn(TAG, 'drain failed — records scheduled for retry', { status, delay });
  }

  private extractHttpStatus(err: unknown): number | null {
    if (err && typeof err === 'object' && 'status' in err) {
      const s = (err as { status: unknown }).status;
      return typeof s === 'number' ? s : null;
    }
    return null;
  }
}
